import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { Table } from '@grnhse/seedling/lib/birch/components/table/table';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import SkeletonLoader from '@grnhse/seedling/lib/birch/components/skeleton_loader';
import { EmptyState } from '@grnhse/seedling/lib/birch/components/empty_state';
import FingerprintOne from '@grnhse/seedling/lib/birch/illustrations/fingerprint_one';
import DataGridPagination from '@grnhse/seedling/lib/birch/components/table/components/pagination';
import { GridFooterContainer } from '@mui/x-data-grid';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import {
  CellParamsDef,
  ColumnDef,
} from '@grnhse/seedling/lib/birch/components/table';
import {
  GridApiCommon,
  GridRowSelectionModel,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid';
import { withLocalePrefix } from 'shared/utils/translation';
import TablePaginationControls from 'talent_matching/components/table/table_pagination_controls';

const t = withLocalePrefix('talent_matching');
const tNoResults = withLocalePrefix('talent_matching.table.no_results');

const StyledTable = styled(Table)`
  .highlighted-row {
    background-color: ${FullPalette.GREY_E500};
  }

  .MuiDataGrid-row .show-on-hover {
    visibility: hidden !important;
  }

  .row-hovered .show-on-hover {
    visibility: visible !important;
    background-color: ${Tokens.colors.hover} !important;
  }

  .Mui-selected {
    background-color: ${FullPalette.WHITE} !important;

    &:hover {
      background-color: ${Tokens.colors.hover} !important;
    }
  }
`;

const SkeletonWrapper = styled.div`
  > div {
    > div {
      display: flex;
      width: 100%;
      border-bottom: 1px solid ${FullPalette.GREY_E500};

      > div {
        &:nth-child(1) {
          width: 20%;
        }
        &:nth-child(2) {
          width: 30%;
        }
        &:nth-child(3) {
          width: 50%;
        }
      }
    }
  }
`;

const TableFooterContainer = styled(GridFooterContainer)`
  min-height: 56px;
  height: auto;
  padding: ${Tokens.spacing['1']} ${Tokens.spacing['2']};
  box-sizing: border-box;

  &.MuiDataGrid-withBorderColor {
    border-top: none;
  }
`;

const CustomLoadingOverlay = () => (
  <SkeletonWrapper>
    <SkeletonLoader
      cellHeight={'16px'}
      rows={25}
      columnsPerRow={3}
      spaceBetweenRows={'16px'}
    />
  </SkeletonWrapper>
);

const DefaultNoRowsOverlay = () => (
  <EmptyState
    header={tNoResults('empty')}
    body=""
    Illustration={FingerprintOne}
    size="sm"
  />
);

type TableFooterProps = {
  currentPage: number;
  totalRows: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  useDataGridPagination: boolean;
};

const TableFooter = ({
  currentPage,
  totalRows,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  useDataGridPagination,
}: TableFooterProps) => (
  <TableFooterContainer>
    <Stack
      direction="row"
      justifyContent="flex-end"
      alignItems="center"
      spacing={1}
      flexWrap="wrap"
      sx={{ width: '100%' }}
    >
      {useDataGridPagination ? (
        <>
          {rowsPerPage ? (
            <SecondaryBody metadata>
              {t('table.footer.rows_per_page', { count: rowsPerPage })}
            </SecondaryBody>
          ) : null}
          <DataGridPagination />
        </>
      ) : (
        <TablePaginationControls
          currentPage={currentPage}
          totalRows={totalRows}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}
    </Stack>
  </TableFooterContainer>
);

type BaseDataTableSlotOverrides = {
  row?: React.ComponentType;
  noRowsOverlay?: React.ComponentType;
  loadingOverlay?: React.ComponentType;
};

type BaseDataTableProps = {
  rows: { id: number | string }[];
  columns: ColumnDef[];
  totalRows: number;
  loading: boolean;
  rowsPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  onRowSelected?: (selectedRows: GridRowSelectionModel) => void;
  ariaLabel?: string;
  apiRef?: React.MutableRefObject<GridApiCommon>;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
  onColumnWidthChange?: (params: unknown) => void;
  savedColumnWidthsRef?: React.MutableRefObject<Record<string, number>>;
  onRowClick?: (row: { id: number | string; [key: string]: unknown }) => void;
  getRowClassName?: (params: CellParamsDef) => string;
  footer?: () => React.ReactElement;
  slots?: BaseDataTableSlotOverrides;
  draggableRows?: boolean;
  enableRowSelectionOnClick?: boolean;
  emptyStateMinHeight?: string;
  useDataGridPagination?: boolean;
};

const EMPTY_ROWS: { id: number | string }[] = [];

const BaseDataTable = ({
  rows,
  columns,
  totalRows,
  loading,
  rowsPerPage,
  currentPage,
  onPageChange,
  onRowsPerPageChange,
  onRowSelected,
  ariaLabel = t('title'),
  apiRef,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  onColumnWidthChange,
  savedColumnWidthsRef: externalWidthsRef,
  onRowClick,
  getRowClassName,
  footer,
  slots = {},
  draggableRows = false,
  enableRowSelectionOnClick = false,
  emptyStateMinHeight = '500px',
  useDataGridPagination = true,
}: BaseDataTableProps) => {
  const visibleRows = loading ? EMPTY_ROWS : rows;

  // Track user-resized column widths. When the user resizes a column,
  // we inject explicit `width` into column definitions to prevent the
  // Seedling Table from applying `flex: 1` (which causes width resets
  // on every re-render).
  const internalWidthsRef = useRef<Record<string, number>>({});
  const savedColumnWidthsRef = externalWidthsRef ?? internalWidthsRef;
  const [widthVersion, setWidthVersion] = useState(0);

  const handleColumnWidthChangeInternal = useCallback(
    (params: { colDef: { field: string }; width: number }) => {
      savedColumnWidthsRef.current[params.colDef.field] = params.width;
      setWidthVersion((v) => v + 1);
      if (onColumnWidthChange) {
        onColumnWidthChange(params);
      }
    },
    [onColumnWidthChange] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Reset saved widths when column visibility changes (columns added/removed),
  // so newly shown columns get the default flex layout
  const previousVisibilityModel = useRef(columnVisibilityModel);
  useEffect(() => {
    if (
      JSON.stringify(previousVisibilityModel.current) !==
      JSON.stringify(columnVisibilityModel)
    ) {
      savedColumnWidthsRef.current = {};
      setWidthVersion((v) => v + 1);
      previousVisibilityModel.current = columnVisibilityModel;
    }
  }, [columnVisibilityModel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Inject saved widths into column definitions so the Seedling Table
  // uses explicit widths instead of flex: 1 for user-resized columns
  const columnsWithSavedWidths = useMemo(() => {
    const widths = savedColumnWidthsRef.current;
    if (Object.keys(widths).length === 0) {
      return columns;
    }
    return columns.map((col) => {
      const savedWidth = widths[col.field];
      if (savedWidth) {
        return { ...col, width: savedWidth, flex: 0 };
      }
      return col;
    });
  }, [columns, widthVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Normalize column visibility model to control draggable handle
  const normalizedColumnVisibilityModel = useMemo(() => {
    if (!columnVisibilityModel) {
      return { draggableHandle: draggableRows };
    }
    return {
      ...columnVisibilityModel,
      draggableHandle: draggableRows,
    };
  }, [columnVisibilityModel, draggableRows]);

  return (
    <StyledTable
      key={rowsPerPage}
      apiRef={apiRef as React.MutableRefObject<GridApiCommon> | undefined}
      ariaLabel={ariaLabel}
      ariaLabelledBy={ariaLabel}
      toolbar={() => <></>}
      columnVisibilityModel={normalizedColumnVisibilityModel}
      onColumnVisibilityModelChange={onColumnVisibilityModelChange}
      onColumnWidthChange={
        handleColumnWidthChangeInternal as React.ComponentProps<
          typeof StyledTable
        >['onColumnWidthChange']
      }
      rowSelection={
        onRowSelected
          ? {
              onRowSelected,
              disableRowSelectionOnClick: !enableRowSelectionOnClick,
            }
          : undefined
      }
      // Without an explicit selection feature, Seedling's Table defaults to
      // selecting a row on click, which applies `.Mui-selected` (white) and masks
      // the grey `.highlighted-row`. Disable click selection unless a caller opts in.
      disableRowSelectionOnClick={!enableRowSelectionOnClick}
      {...(useDataGridPagination
        ? {
            pagination: {
              onPageChange,
              rowsPerPage,
              totalRows,
              currentPage: currentPage - 1,
            },
          }
        : {})}
      onRowClick={onRowClick}
      getRowClassName={getRowClassName}
      columns={columnsWithSavedWidths}
      resizableColumns={!loading && visibleRows.length > 0}
      loading={loading}
      rows={visibleRows}
      sx={{
        minHeight: loading || rows.length === 0 ? emptyStateMinHeight : 'unset',
      }}
      slots={{
        loadingOverlay: slots.loadingOverlay ?? CustomLoadingOverlay,
        noRowsOverlay: slots.noRowsOverlay ?? DefaultNoRowsOverlay,
        ...(slots.row && { row: slots.row as React.ComponentType }),
      }}
      footer={
        footer ??
        (() =>
          (
            <TableFooter
              currentPage={currentPage}
              totalRows={totalRows}
              rowsPerPage={rowsPerPage}
              onPageChange={onPageChange}
              onRowsPerPageChange={onRowsPerPageChange}
              useDataGridPagination={useDataGridPagination}
            />
          ) as React.ReactElement)
      }
    />
  );
};

export default BaseDataTable;
